import { supabase } from '../config/supabase';
import { isFinancialPreviewEnabled, type FinancialPage } from './FinancialReadService';

export type FinancialContractTab = 'current' | 'inactive' | 'pending' | 'all';

function contractError(error: { code?: string; message?: string }): Error {
  if (error.code === '42501') return new Error('Seu perfil não permite acessar os contratos desta clínica.');
  if (error.code === 'PGRST202' || error.code === '42883') return new Error('A atualização de contratos permanentes precisa ser aplicada no banco (46a).');
  return new Error(error.message || 'Não foi possível atualizar os contratos.');
}

export async function readFinancialContractsPage(input: {
  clientId: string; tab: FinancialContractTab; patientId: string | null;
  search: string; offset: number; type: string; status: string; from: string; until: string;
}, signal: AbortSignal): Promise<FinancialPage> {
  if (!input.clientId || !isFinancialPreviewEnabled(input.clientId)) throw new Error('Consulta financeira não habilitada neste ambiente e clínica.');
  const { data, error } = await supabase.rpc('financial_contracts_read', {
    p_client_id: input.clientId, p_tab: input.tab, p_patient_id: input.patientId,
    p_search: input.search, p_offset: input.offset, p_type: input.type, p_status: input.status,
    p_from: input.from || null, p_until: input.until || null,
  }).abortSignal(signal);
  if (error) throw contractError(error);
  if (!data || !Array.isArray(data.records) || typeof data.total !== 'number' || data.pageSize !== 50 || data.records.some((row: { id?: unknown } | null) => !row || typeof row.id !== 'string')) {
    throw new Error('A consulta de contratos retornou um formato inesperado.');
  }
  return data as FinancialPage;
}

export async function endFinancialContract(clientId: string, contractId: string): Promise<void> {
  const { error } = await supabase.rpc('financial_contract_end', { p_client_id: clientId, p_contract_id: contractId });
  if (error) throw contractError(error);
}

export async function copyFinancialContract(input: {
  clientId: string; sourceContractId: string; effectiveFrom: string;
  effectiveUntil?: string | null; payerLinkId?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_contract_copy', {
    p_client_id: input.clientId, p_source_contract_id: input.sourceContractId,
    p_effective_from: input.effectiveFrom, p_effective_until: input.effectiveUntil || null,
    p_payer_link_id: input.payerLinkId || null,
  });
  if (error) {
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A cópia de contratos inativos precisa ser aplicada no banco (48a).');
    throw contractError(error);
  }
  if (typeof data !== 'string' || !data.trim()) throw new Error('A criação do novo contrato retornou um identificador inválido.');
  return data;
}
